import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import { Box, Text, Button, Loader } from '@rsos/base-components';
import { ErrorComponent } from '@rsos/components/capstone/base/form/inputs/MultiEntryInput/MultiEntryInput.styles';
import { GPX_FEATURES } from '@rsos/gainsight';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  expand,
  setSelectedTab,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { CHAT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import {
  ROADSIDE_ASSISTANCE_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import {
  setSelectedFeature,
  setTempMessage,
} from '@rsos/reduxDataStore/src/features/outboundSms/outboundSmsSlice';
import {
  useCreateConversationOutboundAgeroMutation,
  useLazyGetConversationMetadataQuery,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { cleanPhoneNumber } from '@rsos/utils/phoneNumberUtils';
import {
  trackRSASendMessageClick,
  trackRSACustomLinkFetched,
} from '../../../../gainsightTrackings/roadsideAssistanceTrackings';
import { trackStartTF911Response } from '../../CallChats/textFrom911Trackings';
import {
  INVALID_NUMBER,
  MSG_REQUEST_ERROR,
  INVALID_NUMBER_ERROR,
  EXISTING_CONVERSATION,
  MESSAGE_FAILED_TO_SEND,
  EXISTING_CONVERSATION_ERROR,
  ROADSIDE_ASSISTANCE_ERROR,
  ROADSIDE_ASSISTANCE_FAILED,
} from '../OutboundSMS/constants';
import {
  initialValues,
  validationSchema,
  phoneNumberValidator,
} from '../OutboundSMS/helpers';
import {
  Container,
  PhoneNumberContainer,
  SendMsgContainer,
  TextArea,
  CustomMultiEntryInput,
  TextAreaContainer,
} from '../OutboundSMS/styles/OutboundSMS.styles';
import { LoaderBox } from './RoadsideAssistance.styles';
import getRoadsideAssistanceLink from './helpers/getRoadsideAssistanceLink';

const formatNumberWithParam = phone => {
  const cleaned = phone.replace(/\D/g, '');
  const countryCode = cleaned[0];
  const areaCode = cleaned.slice(1, 4);
  const prefix = cleaned.slice(4, 7);
  const lineNumber = cleaned.slice(7, 11);

  return `${countryCode} (${areaCode}) ${prefix}-${lineNumber}`;
};

const PrefixComponent = (
  <Text
    pl="6px"
    mb="5px"
    size="normal"
    color="secondaryText"
    wordBreak="keep-all"
  >
    To:
  </Text>
);

const initialErrorState = {
  [MSG_REQUEST_ERROR]: false,
  [INVALID_NUMBER_ERROR]: false,
  [EXISTING_CONVERSATION_ERROR]: false,
  [ROADSIDE_ASSISTANCE_ERROR]: false,
};
export const RoadsideAssistance = () => {
  const textAreaContainerRef = useRef(null);
  const setFieldValueRef = useRef(null);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [phoneNumberTags, setPhoneNumberTags] = useState([]);
  const [errorMsgs, setErrorMsgs] = useState(initialErrorState);
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const currentPsapAccountId = useSelector(
    state => state.psaps.currentPSAP.account_id,
  );

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      ).name,
  );

  const handleChange = (e, setFieldValue) => {
    const { value } = e.target;
    setFieldValue('initialOutboundMessage', value);
    setErrorMsgs({
      ...errorMsgs,
      [MSG_REQUEST_ERROR]: false,
    });

    if (textAreaContainerRef.current?.dataset) {
      textAreaContainerRef.current.dataset.replicatedValue = value;
    }
  };

  const [createConversation, conversationResult] =
    useCreateConversationOutboundAgeroMutation();
  const [getConversationMetadata] = useLazyGetConversationMetadataQuery();

  const handleSubmit = values => {
    dispatch(setSelectedFeature(ROADSIDE_ASSISTANCE_CARD));
    const phoneNumberTag = values?.phoneNumberTags?.[0];
    const message = values?.initialOutboundMessage;
    const phoneNumber = cleanPhoneNumber(phoneNumberTag?.value);
    const callerID = phoneNumber?.startsWith('1')
      ? phoneNumber
      : `1${phoneNumber}`;

    const opt = {
      callerID,
      message,
      orgName,
    };

    trackRSASendMessageClick({ phoneNumber });
    // replace to track roadside_assistance

    createConversation(opt).then(resp => {
      trackStartTF911Response({
        feature: GPX_FEATURES.TF911_OUTBOUND,
        responseSuccess: !resp?.error,
        conversationId: resp?.data?.conversation_id,
      });
      if (resp?.error) {
        if (resp.error?.status === 409) {
          getConversationMetadata({
            callerID,
            orgName,
            custom: { isOutboundSMS: true },
          }).then(resp => {
            if (resp?.isSuccess) {
              // TODO: logic on success
              // setSelectedCallerID
            }
          });
          dispatch(setSelectedCallerID(callerID));
          dispatch(setSelectedTab(CHAT_TAB));
          dispatch(closeCard(ROADSIDE_ASSISTANCE_CARD));
          dispatch(setTempMessage(message));
          dispatch(expand());

          setErrorMsgs({
            ...errorMsgs,
            [EXISTING_CONVERSATION_ERROR]: true,
          });
        } else {
          setErrorMsgs({
            ...errorMsgs,
            [MSG_REQUEST_ERROR]: true,
          });
        }
      } else {
        // TODO: logic on success
        dispatch(closeCard(ROADSIDE_ASSISTANCE_CARD));
        dispatch(setSelectedCallerID(callerID));
        dispatch(setSelectedTab(CHAT_TAB));
        dispatch(expand());
      }
    });
  };

  const handleUpdateTag = phoneNumberTags => {
    setPhoneNumberTags(phoneNumberTags);
    setErrorMsgs({
      ...errorMsgs,
      [INVALID_NUMBER_ERROR]: false,
      [EXISTING_CONVERSATION_ERROR]: false,
      [ROADSIDE_ASSISTANCE_ERROR]: false,
    });
  };

  const inputError = useMemo(() => {
    if (errorMsgs[EXISTING_CONVERSATION_ERROR]) {
      return EXISTING_CONVERSATION;
    }
    if (errorMsgs[INVALID_NUMBER_ERROR]) {
      return INVALID_NUMBER;
    }
    if (errorMsgs[ROADSIDE_ASSISTANCE_ERROR]) {
      return ROADSIDE_ASSISTANCE_FAILED;
    }
    return null;
  }, [errorMsgs]);

  const isBtnDisabled = useCallback(
    (errors, values) => {
      return (
        errors.phoneNumberTags ||
        errors.initialOutboundMessage ||
        !values?.initialOutboundMessage?.length ||
        (!values.phoneNumberTags.length && !phoneNumberTags.length)
      );
    },
    [phoneNumberTags],
  );

  useEffect(() => {
    if (phoneNumberTags.length === 1) {
      const phoneNumber = phoneNumberTags[0];
      if (phoneNumber.isValid) {
        setIsLoading(true);
        getRoadsideAssistanceLink(phoneNumber, currentPsapAccountId).then(
          resp => {
            if (resp?.data?.detail) {
              // uses the setFieldValueRef === setFieldValue
              if (setFieldValueRef.current) {
                handleChange(
                  {
                    target: {
                      value: `Hello, please click the link below or call 800-378-7080 to request roadside assistance from the dispatch company, Drivers Premier. Please note you may receive additional text messages relating to your request.\n${resp.data.detail}`,
                    },
                  },
                  setFieldValueRef.current,
                );
              }

              trackRSACustomLinkFetched({
                phoneNumber: cleanPhoneNumber(phoneNumber.value),
                roadsideAssistanceUrl: resp.data.detail,
              });
            } else {
              setErrorMsgs({
                ...errorMsgs,
                [ROADSIDE_ASSISTANCE_ERROR]: true,
              });
            }
            setIsLoading(false);
          },
        );
      }
    }
  }, [phoneNumberTags]); // eslint-disable-line

  useEffect(() => {
    if (selectedCallerID) {
      const tag = [
        {
          id: selectedCallerID,
          isValid: true,
          value: formatNumberWithParam(selectedCallerID),
        },
      ];
      handleUpdateTag(tag);
      if (setFieldValueRef.current) {
        setFieldValueRef.current('phoneNumberTags', tag);
      }
    }
  }, [selectedCallerID]); // eslint-disable-line

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors }) => {
        setFieldValueRef.current = setFieldValue; // set it as ref to be use outside
        return (
          <Form id="roadside_assistance-sms">
            <Container>
              <PhoneNumberContainer>
                <Box>
                  <Text fontWeight="bold" size="large" color="primaryText">
                    Request Roadside Assistance
                  </Text>
                  <Text size="normal" color="secondaryText">
                    Send a roadside assistance link to a caller
                  </Text>
                </Box>
                <Box width="100%" py={3}>
                  <CustomMultiEntryInput
                    value={values.phoneNumber}
                    name="phoneNumber"
                    placeholder={
                      phoneNumberTags.length ? '' : '+1 555 555 5555'
                    }
                    tags={phoneNumberTags}
                    updateTags={handleUpdateTag}
                    setFieldValue={setFieldValue}
                    validator={phoneNumberValidator}
                    inputType="tel"
                    hiddenInputName="phoneNumberTags"
                    error={inputError ? inputError : errors?.phoneNumberTags}
                    disabled={!!phoneNumberTags.length}
                    prefixComponent={PrefixComponent}
                  />
                </Box>
              </PhoneNumberContainer>
              <SendMsgContainer minHeight="60px">
                <TextAreaContainer ref={textAreaContainerRef}>
                  <TextArea
                    rows={1}
                    data-name="roadside_assistance-textbox"
                    name="initialOutboundMessage"
                    onChange={e => handleChange(e, setFieldValue)}
                    placeholder="Message"
                    value={values.initialOutboundMessage}
                    maxHeight={104}
                    error={
                      errors?.initialOutboundMessage ||
                      errorMsgs[MSG_REQUEST_ERROR]
                    }
                    disabled={conversationResult?.isLoading}
                  />
                  {errors?.initialOutboundMessage ? (
                    <ErrorComponent error={errors.initialOutboundMessage} />
                  ) : errorMsgs[MSG_REQUEST_ERROR] ? (
                    <ErrorComponent error={MESSAGE_FAILED_TO_SEND} />
                  ) : null}
                </TextAreaContainer>

                <Button
                  dataName="roadside_assistance-submit"
                  type="submit"
                  btnType="primary"
                  form="roadside_assistance-sms"
                  mb={0}
                  mr={0}
                  disabled={isBtnDisabled(errors, values)}
                  isLoadingProp={conversationResult.isLoading}
                >
                  Send
                </Button>
                {isLoading && (
                  <LoaderBox align="center" justify="center">
                    <Loader fluid text="Getting Roadside Assistance" />
                  </LoaderBox>
                )}
              </SendMsgContainer>
            </Container>
          </Form>
        );
      }}
    </Formik>
  );
};
